// -----------------------------------------------------------------------------
// core/model.ts — the data shapes that ARE the project.
//
// This is the single source of truth for what a ZeroFrame document contains:
// a project (canvas size/fps/background) with tracks, each holding clips, plus a
// separate library of imported media assets. Everything here is plain,
// JSON-serializable data — no DOM nodes, no class instances — so the whole
// document round-trips through serialize.ts as the save format (CLAUDE.md §4).
//
// Keyframes / effects / transitions land in later tiers; the types are kept
// small and additive so Tier 1/2 can extend them without a rewrite.
// -----------------------------------------------------------------------------

export type ID = string;

export const newId = (): ID => crypto.randomUUID();

export type TrackKind = 'video' | 'audio' | 'text';
export type ClipKind = 'video' | 'image' | 'audio' | 'text';

// A piece of imported media in the library. The raw bytes are NOT stored here —
// they live in the engine's media layer and (optionally) scratch storage. This
// is just the metadata the timeline and serializer need.
export interface MediaAsset {
  id: ID;
  name: string;
  kind: 'video' | 'image' | 'audio';
  mimeType: string;
  durationSec: number; // intrinsic source duration (0 for stills)
  width: number;
  height: number;
  fps: number;
  hasAudio: boolean;
  scratchKey: string | null; // key into scratch storage, if persisted
}

// Fields every clip shares.
export interface ClipBase {
  id: ID;
  name: string;
  startSec: number; // position on the timeline
  durationSec: number; // visible length on the timeline
}

// A clip backed by a library asset (video / image / audio).
export interface MediaClip extends ClipBase {
  kind: 'video' | 'image' | 'audio';
  assetId: ID;
  inSec: number; // trim in-point within the source media
}

// A text overlay clip — self-contained, no asset needed.
export interface TextClip extends ClipBase {
  kind: 'text';
  text: string;
  fontSize: number; // px, expressed at project resolution
  color: string; // CSS colour
  fontFamily: string;
  xFrac: number; // 0..1 horizontal centre across the frame
  yFrac: number; // 0..1 vertical centre down the frame
}

export type Clip = MediaClip | TextClip;

export interface Track {
  id: ID;
  kind: TrackKind;
  name: string;
  clips: Clip[];
}

export interface Project {
  id: ID;
  name: string;
  width: number;
  height: number;
  fps: number;
  background: string; // canvas background colour (CSS)
  tracks: Track[];
}

// ---- small pure queries ------------------------------------------------------

export function clipEnd(clip: Clip): number {
  return clip.startSec + clip.durationSec;
}

// The latest clip end across all tracks = the project's content duration.
export function projectDuration(project: Project): number {
  let end = 0;
  for (const track of project.tracks) {
    for (const clip of track.clips) end = Math.max(end, clipEnd(clip));
  }
  return end;
}

export function findTrack(project: Project, trackId: ID): Track | undefined {
  return project.tracks.find((t) => t.id === trackId);
}

// Locate a clip plus the track it lives in and its index within that track.
export interface ClipLocation {
  track: Track;
  clip: Clip;
  index: number;
}
export function locateClip(project: Project, clipId: ID): ClipLocation | undefined {
  for (const track of project.tracks) {
    const index = track.clips.findIndex((c) => c.id === clipId);
    if (index >= 0) return { track, clip: track.clips[index], index };
  }
  return undefined;
}

// Keep a track's clips ordered by start time — the timeline and render plan both
// assume this ordering.
export function sortTrackClips(track: Track): void {
  track.clips.sort((a, b) => a.startSec - b.startSec);
}

// A sensible empty project: 1080p30 with one video and one text track.
export function createDefaultProject(): Project {
  return {
    id: newId(),
    name: 'Untitled project',
    width: 1920,
    height: 1080,
    fps: 30,
    background: '#000000',
    tracks: [
      { id: newId(), kind: 'video', name: 'Video 1', clips: [] },
      { id: newId(), kind: 'text', name: 'Text 1', clips: [] },
    ],
  };
}
